/**
 * Ripia Token Storage Utilities
 *
 * トークン・テナントコンテキストの永続化管理
 * 12種類のストレージキーを一元管理
 * @module auth/shared/tokenStorage
 */

import {
  saveEncryptedToken,
  loadEncryptedToken,
  removeEncryptedToken,
} from '../../../utils/crypto';
import logger from '../../../utils/logger';
import {
  TOKEN_KEYS,
  LEGACY_KEY_MIGRATIONS,
  RIPIA_STORAGE_PREFIXES,
} from '../constants';
import type { LoginResponse, User } from '../types';
import type { TenantContext, TenantInfo } from '../../../types/multiTenant';

/**
 * Token Storage Manager
 * 全トークン・セッション状態の一元管理
 */
export const TokenStorage = {
  // ========================
  // Access Token Operations
  // ========================

  /**
   * ログインレスポンスからトークンを保存
   */
  saveTokens(response: LoginResponse, rememberMe: boolean = true): void {
    try {
      saveEncryptedToken(response.accessToken, TOKEN_KEYS.ACCESS_TOKEN);
      saveEncryptedToken(response.refreshToken, TOKEN_KEYS.REFRESH_TOKEN);
      saveEncryptedToken(JSON.stringify(response.user), TOKEN_KEYS.USER_DATA);

      if (rememberMe) {
        localStorage.setItem(TOKEN_KEYS.REMEMBER_ME, 'true');
      }

      logger.info('[TokenStorage] トークン保存完了');
    } catch (error) {
      logger.error('[TokenStorage] トークン保存エラー:', error);
      throw error;
    }
  },

  /**
   * 新しいアクセストークンを保存（リフレッシュ時）
   */
  saveNewAccessToken(accessToken: string): void {
    saveEncryptedToken(accessToken, TOKEN_KEYS.ACCESS_TOKEN);
    logger.info('[TokenStorage] AccessToken更新完了');
  },

  /**
   * アクセストークンを取得
   */
  getAccessToken(): string | null {
    return loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
  },

  /**
   * リフレッシュトークンを取得
   */
  getRefreshToken(): string | null {
    return loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
  },

  /**
   * ユーザーデータを取得
   */
  getUserData(): User | null {
    const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
    if (!userData) return null;
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  },

  // ========================
  // Token Clearing Operations
  // ========================

  /**
   * 基本トークンをクリア
   */
  clearTokens(): void {
    removeEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    removeEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
    removeEncryptedToken(TOKEN_KEYS.USER_DATA);
    localStorage.removeItem(TOKEN_KEYS.REMEMBER_ME);
    logger.info('[TokenStorage] 基本トークンクリア完了');
  },

  /**
   * マルチテナントトークンをクリア
   */
  clearMultiTenantTokens(): void {
    removeEncryptedToken(TOKEN_KEYS.TENANT_CONTEXT);
    removeEncryptedToken(TOKEN_KEYS.AVAILABLE_TENANTS);
    removeEncryptedToken(TOKEN_KEYS.SELECTED_TENANT);
    logger.info('[TokenStorage] マルチテナントトークンクリア完了');
  },

  /**
   * エンタープライズトークンをクリア
   */
  clearEnterpriseTokens(): void {
    removeEncryptedToken(TOKEN_KEYS.JWT_JTI);
    removeEncryptedToken(TOKEN_KEYS.DEVICE_FINGERPRINT);
    removeEncryptedToken(TOKEN_KEYS.SECURITY_LEVEL);
    removeEncryptedToken(TOKEN_KEYS.SESSION_ID);
    removeEncryptedToken(TOKEN_KEYS.DEVICE_TRUST_SCORE);
    removeEncryptedToken(TOKEN_KEYS.LAST_ACTIVITY);
    logger.info('[TokenStorage] エンタープライズトークンクリア完了');
  },

  /**
   * 全トークンをクリア
   */
  clearAllTokens(): void {
    this.clearTokens();
    this.clearMultiTenantTokens();
    this.clearEnterpriseTokens();
    logger.info('[TokenStorage] 全トークンクリア完了');
  },

  // ========================
  // Tenant Context Operations
  // ========================

  /**
   * テナントコンテキストを保存
   */
  saveTenantContext(context: TenantContext): void {
    saveEncryptedToken(JSON.stringify(context), TOKEN_KEYS.TENANT_CONTEXT);
    logger.info('[TokenStorage] テナントコンテキスト保存完了');
  },

  /**
   * テナントコンテキストを取得
   */
  loadTenantContext(): TenantContext | null {
    const contextStr = loadEncryptedToken(TOKEN_KEYS.TENANT_CONTEXT);
    if (!contextStr) return null;
    try {
      return JSON.parse(contextStr);
    } catch {
      return null;
    }
  },

  /**
   * 利用可能なテナント一覧を保存
   */
  saveAvailableTenants(tenants: TenantInfo[]): void {
    saveEncryptedToken(JSON.stringify(tenants), TOKEN_KEYS.AVAILABLE_TENANTS);
    logger.info('[TokenStorage] 利用可能テナント一覧保存完了');
  },

  /**
   * 利用可能なテナント一覧を取得
   */
  loadAvailableTenants(): TenantInfo[] {
    const tenantsStr = loadEncryptedToken(TOKEN_KEYS.AVAILABLE_TENANTS);
    if (!tenantsStr) return [];
    try {
      return JSON.parse(tenantsStr);
    } catch {
      return [];
    }
  },

  /**
   * 選択中のテナントIDを保存
   */
  saveSelectedTenant(tenantId: string): void {
    saveEncryptedToken(tenantId, TOKEN_KEYS.SELECTED_TENANT);
  },

  /**
   * 選択中のテナントIDを取得
   */
  getSelectedTenant(): string | null {
    return loadEncryptedToken(TOKEN_KEYS.SELECTED_TENANT);
  },

  // ========================
  // Enterprise Security Operations
  // ========================

  /**
   * エンタープライズトークン情報を保存
   */
  saveEnhancedTokens(data: {
    accessToken: string;
    refreshToken: string;
    user: User;
    jti: string;
    deviceFingerprint: string;
    securityLevel: 'standard' | 'high' | 'premium';
  }): void {
    saveEncryptedToken(data.accessToken, TOKEN_KEYS.ACCESS_TOKEN);
    saveEncryptedToken(data.refreshToken, TOKEN_KEYS.REFRESH_TOKEN);
    saveEncryptedToken(JSON.stringify(data.user), TOKEN_KEYS.USER_DATA);
    saveEncryptedToken(data.jti, TOKEN_KEYS.JWT_JTI);
    saveEncryptedToken(data.deviceFingerprint, TOKEN_KEYS.DEVICE_FINGERPRINT);
    saveEncryptedToken(data.securityLevel, TOKEN_KEYS.SECURITY_LEVEL);
    logger.info('[TokenStorage] エンタープライズトークン保存完了');
  },

  /**
   * セキュリティレベルを取得
   */
  getSecurityLevel(): 'standard' | 'high' | 'premium' {
    const level = loadEncryptedToken(TOKEN_KEYS.SECURITY_LEVEL);
    if (level === 'high' || level === 'premium') return level;
    return 'standard';
  },

  /**
   * デバイスフィンガープリントを取得
   */
  getDeviceFingerprint(): string | null {
    return loadEncryptedToken(TOKEN_KEYS.DEVICE_FINGERPRINT);
  },

  /**
   * JWT IDを取得
   */
  getJwtId(): string | null {
    return loadEncryptedToken(TOKEN_KEYS.JWT_JTI);
  },

  // ========================
  // Activity Tracking
  // ========================

  /**
   * 最終アクティビティを更新
   */
  updateLastActivity(): void {
    saveEncryptedToken(Date.now().toString(), TOKEN_KEYS.LAST_ACTIVITY);
  },

  /**
   * 最終アクティビティを取得
   */
  getLastActivity(): number | null {
    const activity = loadEncryptedToken(TOKEN_KEYS.LAST_ACTIVITY);
    return activity ? parseInt(activity, 10) : null;
  },
};

/**
 * Ripia関連ストレージのみクリア
 * FIX Issue #930: localStorage.clear()は他サイトのデータも消すため、
 * プレフィックスベースでクリア
 */
export const clearRipiaStorage = (): void => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (
        RIPIA_STORAGE_PREFIXES.some(
          (prefix) =>
            key.startsWith(prefix) || key.toLowerCase().includes('ripia')
        )
      ) {
        localStorage.removeItem(key);
      }
    });

    Object.keys(sessionStorage).forEach((key) => {
      if (
        RIPIA_STORAGE_PREFIXES.some(
          (prefix) =>
            key.startsWith(prefix) || key.toLowerCase().includes('ripia')
        )
      ) {
        sessionStorage.removeItem(key);
      }
    });

    logger.info('[TokenStorage] Ripia関連ストレージをクリアしました');
  } catch (e) {
    logger.error('[TokenStorage] Failed to clear storage:', e);
  }
};

/**
 * 既存ユーザーのLocalStorageキーを移行
 * アプリ起動時に一度だけ実行される
 */
export const migrateLocalStorageKeys = (): void => {
  const migrationFlag = 'ripia_key_migration_v3_completed';

  // 既に移行済みの場合はスキップ
  if (localStorage.getItem(migrationFlag)) {
    return;
  }

  try {
    for (const { oldKey, newKey } of LEGACY_KEY_MIGRATIONS) {
      const oldData = localStorage.getItem(oldKey);
      const newData = localStorage.getItem(newKey);

      // 古いキーにデータがあり、新しいキーにデータがない場合のみ移行
      if (oldData && !newData) {
        localStorage.setItem(newKey, oldData);
        logger.info(
          `[Issue #795] Migrated LocalStorage key: ${oldKey} -> ${newKey}`
        );
      }
    }

    // 移行完了フラグを設定
    localStorage.setItem(migrationFlag, new Date().toISOString());
    logger.info('[Issue #795] LocalStorage key migration completed');
  } catch (error) {
    logger.error('[Issue #795] LocalStorage key migration failed:', error);
  }
};

// アプリ起動時に移行を実行（ブラウザ環境のみ）
if (typeof window !== 'undefined') {
  migrateLocalStorageKeys();
}

export default TokenStorage;
